/**
 * The PDApps shell (#1484) — a program of its own on the taskbar (and a phone
 * tab): every PDApp the person can get back to. Quick access on top, the full
 * lists below:
 *
 *   Favorites — what they starred (stored as pins); stays at the top, never ages out.
 *   Recent   — every PDApp they opened, in ANY org or through a link (personal:
 *              it follows the account, not the active org), newest first. ONE
 *              row that fits the window; "View all" opens the rest. Kept
 *              indefinitely; remove an entry or clear it.
 *   Yours · Shared with me — two tabs, A→Z: the PDApps they can use in the org
 *              they're in now. Shared = grants, other orgs' shares, links that
 *              name them. (A public link isn't shared WITH anyone: it shows in
 *              Recent once opened.)
 *
 * Searching shows one grid of every match instead.
 *
 * Deliberately bare: a tile is its icon and name, and RUNS its app; its ⋯ menu
 * offers Run, Share (where the person may share it), Pin and Remove. A LINK app
 * offers no Share and nothing that opens its files — a link share means "run it,
 * don't read its internals". Everything else stays on Drive → PDApps, which the
 * header links to.
 *
 * Every list arrives judged by the server, and Run / Share go through the routes
 * and dialog they always did — this view decides no access.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Search, User, UsersRound, Star, StarOff, Clock, MoreHorizontal, Play, Share2, ArrowUpRight, Trash2, Link2, Globe, ChevronRight } from 'lucide-react'
import { parseMiniappIcon, parseMiniappIconRev, isMiniappFolder, isSharedPointer } from '@papan/types'
import { useTranslation } from '@papan/i18n'
import { usePageTitle } from '@papan/brand/react'
import { ShareLinksDialog } from '@papan/share-links/ui'
import {
  MiniappIcon, PDAppIcon, FileContextMenu, useAnchoredPosition, useRefreshWhenShown, useTheme, getThemeClass,
  useConfirm, useToast, type FileMenuItem,
} from '@papan/core-ui/ui'
import { useProjects, useIncomingSharedProjects, type ProjectItem } from '../home/data'
import { formatRelativeDate } from '../formatters'
import {
  appName, buildOrgApps, filterEntries, fetchHistoryPage, usePdappHistory, useSharedLinkApps,
  openLinkApp, pinActiveApp, pinSharedLink, setEntryPinned, removeEntry, clearHistory, locateLinkApp, takeLocateFragment,
  type PdappEntry,
} from './appsData'

/** A tile's icon, as on the launcher. */
const TILE_ICON_PX = 64

type CollectionId = 'mine' | 'shared'
const COLLECTION_KEY = 'papan.pdapps.collection'
const readCollection = (): CollectionId | null => {
  try { const v = localStorage.getItem(COLLECTION_KEY); return v === 'mine' || v === 'shared' ? v : null } catch { return null }
}
const saveCollection = (c: CollectionId) => { try { localStorage.setItem(COLLECTION_KEY, c) } catch { /* per-viewer convenience only */ } }

/** A→Z by name — how the full lists read, so they never repeat the Recent row's order. */
const byName = (a: TileView, b: TileView) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })

/**
 * How many columns a CSS grid has right now — the drive home's way of filling
 * exactly one row of cards. A callback ref, since the grid mounts late.
 */
function useGridColumns(initial: number): [(el: HTMLDivElement | null) => void, number] {
  const [el, setEl] = useState<HTMLDivElement | null>(null)
  const [cols, setCols] = useState(initial)
  useEffect(() => {
    if (!el) return
    const measure = () => {
      const n = getComputedStyle(el).gridTemplateColumns.split(' ').filter(Boolean).length
      if (n > 0) setCols(n)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [el])
  return [setEl, cols]
}

export interface AppsViewProps {
  urlPrefix: string
  /** The active tenant — decides which entries run here and which switch org. */
  tenantId: string
  isOrg: boolean
  orgEmail?: string | null
  /** The active org's name, for its section heading. */
  orgName?: string | null
  /** Is this window on screen? The shell keeps hidden windows mounted. */
  deckActive?: boolean
  /**
   * An identity-only tenant has no drive: the window shows only the person's own
   * Pinned and Recent (which follow the account, so they hold what they opened
   * as their other identities) — no org part, no Share, no "Manage in Drive".
   */
  historyOnly?: boolean
  /**
   * Run an app of ANOTHER org whose entry was judged as one of the person's several
   * identities there, none chosen yet this session: the shell's tenant hook makes
   * that identity the org's choice ONLY IF STILL UNSET (a choice made since the list
   * was read is never undone), then the app loads fresh as whatever is chosen.
   * Absent: the app just opens (the org then picks the identity itself).
   */
  chooseIdentityIfUnset?: (tenantId: string, email: string) => Promise<boolean>
}

/** What a tile stands for — the three sources, one tile component. */
type Tile =
  | { type: 'entry'; entry: PdappEntry }
  | { type: 'org'; app: ProjectItem }

interface TileView {
  id: string
  name: string
  icon: string | null
  /** The folder id an IMAGE icon is fetched by — only for an app of the active org. */
  iconRef: string | null
  /** Pinned / Recent only: when it was last opened, and where it's from. */
  subtitle?: string
  /** Where it's from (its maker, another org, a link's org) — the tile's tooltip. */
  hint?: string
  /** How it reached the person, on the icon's corner: a public link, or a link naming them.
   *  None for an app shared inside the org (the Share dialog's own Globe / Link icons). */
  badge?: 'public' | 'link'
  pinned?: boolean
  tile: Tile
}

export function AppsView({ urlPrefix, tenantId, isOrg, orgEmail, orgName, deckActive = true, historyOnly = false, chooseIdentityIfUnset }: AppsViewProps) {
  const { t } = useTranslation('files')
  const navigate = useNavigate()
  const qc = useQueryClient()
  const confirm = useConfirm()
  const toast = useToast()
  usePageTitle(t('appsWindow.title'), deckActive)

  const projects = useProjects(!historyOnly)
  const incoming = useIncomingSharedProjects(!historyOnly)
  // The shell's own lists run only while the window is SHOWN: a kept-alive hidden
  // window must not re-read them on every app switch elsewhere (each read judges
  // every entry server-side). Shown again, a list that went stale meanwhile reads.
  const history = usePdappHistory(deckActive)
  // One heavy list at a time: each judges its entries server-side (a chain of
  // queries per link), so "Shared with me" starts once the history has answered.
  const sharedLinks = useSharedLinkApps(false, deckActive && !historyOnly && history.isFetched)
  // The drive's lists: looking at the window again is the "look again".
  useRefreshWhenShown(() => { if (!historyOnly) { void projects.refetch(); void incoming.refetch() } })

  // "Show more" pages of the history, appended below the first. Reset whenever the
  // first page changes (a new open, a removal, an identity switch).
  const [morePages, setMorePages] = useState<{ entries: PdappEntry[]; next: string | null } | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  useEffect(() => { setMorePages(null) }, [history.data])
  const nextCursor = morePages ? morePages.next : history.data?.next ?? null
  const showMore = async () => {
    if (!nextCursor || loadingMore) return
    setLoadingMore(true)
    try {
      const page = await fetchHistoryPage(nextCursor)
      setMorePages(m => ({ entries: [...(m?.entries ?? []), ...page.recent], next: page.next }))
    } catch { toast.error(t('appsWindow.error')) } finally { setLoadingMore(false) }
  }

  const [query, setQuery] = useState('')
  const [collection, setCollectionState] = useState<CollectionId | null>(readCollection)
  const setCollection = (next: CollectionId) => { setCollectionState(next); saveCollection(next) }
  const [recentOpen, setRecentOpen] = useState(false)
  const [recentGridRef, recentCols] = useGridColumns(8)
  const orgApps = useMemo(
    () => buildOrgApps([...(projects.data ?? []), ...(incoming.data ?? [])], { orgEmail, isOrg, query }),
    [projects.data, incoming.data, orgEmail, isOrg, query],
  )
  const pinned = useMemo(() => filterEntries(history.data?.pinned ?? [], query), [history.data, query])
  const recent = useMemo(() => filterEntries([...(history.data?.recent ?? []), ...(morePages?.entries ?? [])], query), [history.data, morePages, query])
  const linkShared = useMemo(() => filterEntries(sharedLinks.data ?? [], query), [sharedLinks.data, query])

  /** Is this org app pinned already? Its pin entry, to unpin by. */
  const pinOf = useCallback((appId: string) => (history.data?.pinned ?? [])
    .find(e => e.kind === 'app' && e.app?.appId === appId && e.app.tenantId === tenantId) ?? null, [history.data, tenantId])

  // ── Actions ─────────────────────────────────────────────────────────────
  const runApp = useCallback((appId: string, where?: { tenantId: string; personal: boolean; switchTo?: string }) => {
    // An app of another org runs there: the URL's prefix switches the shell to it.
    const elsewhere = !!where && where.tenantId !== tenantId
    const prefix = !elsewhere ? urlPrefix : where!.personal ? '/~' : `/t/${where!.tenantId}`
    // …as the identity it was listed for, when that org would otherwise pick one itself.
    if (elsewhere && where!.switchTo && chooseIdentityIfUnset) {
      const path = `${prefix}/app/${appId}`
      void chooseIdentityIfUnset(where!.tenantId, where!.switchTo).then((ok) => {
        if (ok) window.location.assign(path)
        else toast.error(t('appsWindow.actionFailed'))
      })
      return
    }
    navigate(`${prefix}/app/${appId}`)
  }, [navigate, tenantId, urlPrefix, toast, t, chooseIdentityIfUnset])
  const run = useCallback((tile: Tile) => {
    if (tile.type === 'org') return runApp(tile.app.folderId)
    const e = tile.entry
    if (e.kind === 'app' && e.app) return runApp(e.app.appId, e.app)
    openLinkApp(qc, e).catch(() => toast.error(t('appsWindow.linkUnavailable')))
  }, [runApp, qc, toast, t])
  // #538 — opened from the identity bar's "Made by": show the PDApp in its folder, if it's in my drive.
  useEffect(() => {
    const token = takeLocateFragment()
    if (!token) return
    locateLinkApp(token)
      .then((path) => { if (path) navigate(path); else toast.info(t('appsWindow.notInDrive')) })
      .catch(() => {})
  }, [navigate, toast, t])
  const guarded = (fn: () => Promise<void>) => { fn().catch(() => toast.error(t('appsWindow.actionFailed'))) }

  const [shareTarget, setShareTarget] = useState<{ id: string; name: string; path: string; isFolder: boolean } | null>(null)

  /**
   * What Share opens for a tile, or null where there is nothing to share: an app of
   * THIS org with its real drive row (the dialog acts in the active org, on the real
   * name and path) — never a link app, never an app of another org.
   */
  const shareFor = (tile: Tile): { id: string; name: string; path: string; isFolder: boolean } | null => {
    if (tile.type === 'org') return { id: tile.app.folderId, name: tile.app.name, path: tile.app.path, isFolder: !isSharedPointer(tile.app.name) }
    const e = tile.entry
    if (e.kind !== 'app' || !e.app || e.app.tenantId !== tenantId) return null
    const appId = e.app.appId
    const row = [...(projects.data ?? []), ...(incoming.data ?? [])].find(p => p.folderId === appId)
    return row ? { id: appId, name: row.name, path: row.path, isFolder: !e.app.mounted } : null
  }

  const menuFor = (tile: Tile): FileMenuItem[] => {
    const items: FileMenuItem[] = [
      { kind: 'item', icon: Play, label: t('appsWindow.run'), testId: 'apps-menu-run', onClick: () => { closeMenu(); run(tile) } },
    ]
    const share = shareFor(tile)
    if (share) items.push({ kind: 'item', icon: Share2, label: t('appsWindow.share'), testId: 'apps-menu-share', onClick: () => { closeMenu(); setShareTarget(share) } })
    if (tile.type === 'org') {
      const p = tile.app
      const pin = pinOf(p.folderId)
      items.push(pin
        ? { kind: 'item', icon: StarOff, label: t('appsWindow.unpin'), testId: 'apps-menu-unpin', onClick: () => { closeMenu(); guarded(() => setEntryPinned(qc, pin.key, false)) } }
        : { kind: 'item', icon: Star, label: t('appsWindow.pin'), testId: 'apps-menu-pin', onClick: () => { closeMenu(); guarded(() => pinActiveApp(qc, p.folderId)) } })
      return items
    }
    const e = tile.entry
    if (e.source === 'shared') {
      items.push({ kind: 'item', icon: Star, label: t('appsWindow.pin'), testId: 'apps-menu-pin', onClick: () => { closeMenu(); guarded(() => pinSharedLink(qc, e.key)) } })
      return items
    }
    items.push(e.pinned
      ? { kind: 'item', icon: StarOff, label: t('appsWindow.unpin'), testId: 'apps-menu-unpin', onClick: () => { closeMenu(); guarded(() => setEntryPinned(qc, e.key, false)) } }
      : { kind: 'item', icon: Star, label: t('appsWindow.pin'), testId: 'apps-menu-pin', onClick: () => { closeMenu(); guarded(() => setEntryPinned(qc, e.key, true)) } })
    items.push({ kind: 'separator' })
    items.push({ kind: 'item', icon: Trash2, label: t('appsWindow.remove'), testId: 'apps-menu-remove', onClick: () => { closeMenu(); guarded(() => removeEntry(qc, e.key)) } })
    return items
  }

  const onClear = async () => {
    const ok = await confirm({ title: t('appsWindow.clear.title'), message: t('appsWindow.clear.message'), confirmText: t('appsWindow.clear.confirm'), type: 'danger' })
    if (ok) guarded(() => clearHistory(qc))
  }

  // ── ONE menu for the window, re-anchored per tile ──────────────────────
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [menu, setMenu] = useState<{ view: TileView; at: { x: number; y: number } | null } | null>(null)
  const anchorRef = useRef<HTMLElement | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const closeMenu = useCallback(() => setMenu(null), [])
  const { pos, exiting, origin } = useAnchoredPosition({
    open: !!menu,
    anchorRef,
    floatingRef: menuRef,
    onDismiss: closeMenu,
    align: 'start',
    exitTransitionMs: 160,
    anchorKey: menu ? `${menu.view.id}:${menu.at ? `${menu.at.x},${menu.at.y}` : 'btn'}` : null,
    anchorRect: menu?.at ? () => new DOMRect(menu.at!.x, menu.at!.y, 0, 0) : undefined,
  })
  const openMenu = (view: TileView, el: HTMLElement, at: { x: number; y: number } | null) => {
    anchorRef.current = el
    setMenu(m => (m && m.view.id === view.id && !at && !m.at ? null : { view, at }))
  }
  // Outside press and Escape close it; so does leaving the window (the menu is
  // portaled to <body>, so it would otherwise float over whatever is shown next).
  useEffect(() => {
    if (!menu) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (menuRef.current?.contains(target)) return
      if (!menu.at && anchorRef.current?.contains(target)) return // the ⋯ button toggles it itself
      setMenu(null)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(null) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [menu])
  useEffect(() => { if (!deckActive) { setMenu(null); setShareTarget(null) } }, [deckActive])

  // ── Tiles ───────────────────────────────────────────────────────────────
  const line = (...parts: Array<string | null | undefined>) => parts.filter(Boolean).join(' · ') || undefined
  /**
   * `show`: Pinned and Recent say where it's from and when it was last opened;
   * Shared with me says only where it's from.
   */
  const entryView = (e: PdappEntry, show: 'when-and-where' | 'where'): TileView => {
    // Where it's from: a public link, a link (and its org), or another org.
    const from = e.kind === 'link'
      ? e.audience === 'public' ? t('appsWindow.publicLink') : e.orgName ? t('appsWindow.namedLink', { org: e.orgName }) : t('appsWindow.viaLink')
      : e.app?.tenantId === tenantId ? null : e.orgName
    return {
      id: `${e.source}:${e.key}`,
      name: e.name,
      icon: e.icon,
      iconRef: e.kind === 'app' && e.app && e.app.tenantId === tenantId && !e.app.mounted ? e.app.appId : null,
      hint: from ?? undefined,
      // Where first: on a narrow tile it's what survives the cut (the row is already newest-first).
      subtitle: show === 'where' ? from ?? undefined : line(from, e.openedAt ? formatRelativeDate(e.openedAt) : null),
      badge: e.kind !== 'link' ? undefined : e.audience === 'public' ? 'public' : 'link',
      pinned: e.pinned,
      tile: { type: 'entry', entry: e },
    }
  }
  /** `from` (Shared with me): who shared it — its maker in this org, or the org that shared it in. */
  const orgView = (p: ProjectItem, from?: string | null): TileView => ({
    id: `org:${p.folderId}`,
    name: appName(p),
    icon: p.metadataJson ?? null,
    // A `.shared` mount's composited PNG isn't reachable from this tenant: draw the spec.
    iconRef: isSharedPointer(p.name) ? null : p.folderId,
    hint: from ?? undefined,
    subtitle: from ?? undefined,
    pinned: !!pinOf(p.folderId),
    tile: { type: 'org', app: p },
  })

  const tileLabels = { more: t('appsWindow.more'), pinned: t('appsWindow.sections.pinned'), viaLink: t('appsWindow.viaLink'), publicLink: t('appsWindow.publicLink') }
  const renderTile = (v: TileView) => (
    <AppTile key={v.id} view={v} menuOpen={menu?.view.id === v.id} onRun={() => run(v.tile)} onMenu={openMenu} labels={tileLabels} />
  )
  /** Tiles in rows, as on the launcher; the columns share the width evenly (3 on a phone). */
  const renderGrid = (views: TileView[], ref?: (el: HTMLDivElement | null) => void) => (
    <div ref={ref} className="grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-x-2 gap-y-4">{views.map(renderTile)}</div>
  )
  const heading = (icon: React.ReactNode, label: string, count: number, extra?: React.ReactNode) => (
    <div className="flex items-center justify-between gap-3 mb-3">
      <h2 className="ws-home-heading flex items-center gap-1.5 min-w-0">
        {icon}{label}<span className="themed-text-muted font-normal">{count}</span>
      </h2>
      {extra}
    </div>
  )

  const loading = history.isLoading || (!historyOnly && (projects.isLoading || incoming.isLoading))
  const pinnedViews = pinned.map(e => entryView(e, 'when-and-where'))
  const recentViews = recent.map(e => entryView(e, 'when-and-where'))
  const mineViews = orgApps.mine.map(p => orgView(p)).sort(byName)
  const sharedViews = [
    ...linkShared.map(e => entryView(e, 'where')),
    ...orgApps.shared.map(p => orgView(p, p.owner?.name)),
  ].sort(byName)
  // The org "Yours" and "Shared with me" belong to.
  const orgLabel = !isOrg ? t('appsWindow.sections.personal') : orgName || null
  // More history the server has not judged yet keeps the list from reading as empty.
  const moreHistory = !!nextCursor && !query.trim()
  const nothing = pinnedViews.length + recentViews.length + sharedViews.length + mineViews.length === 0 && !moreHistory

  const linkBtn = 'text-xs themed-text-muted hover:themed-text flex items-center gap-0.5 transition-colors'

  /** Searching: one grid of every match, each app once (it can be pinned AND in a tab). */
  const results = () => {
    const identity = (v: TileView) => {
      if (v.tile.type === 'org') return `org:${v.tile.app.folderId}`
      const e = v.tile.entry
      if (e.kind === 'app' && e.app) return e.app.tenantId === tenantId ? `org:${e.app.appId}` : `app:${e.app.tenantId}:${e.app.appId}`
      // A link entry carries no app id; two same-named link apps are two apps. A link app both
      // in history and shared with you may show twice — never one hiding another.
      return `${e.source}:${e.key}`
    }
    const seen = new Set<string>()
    const all = [...pinnedViews, ...recentViews, ...mineViews, ...sharedViews]
      .filter(v => { const k = identity(v); if (seen.has(k)) return false; seen.add(k); return true })
      .map(v => ({ ...v, subtitle: undefined }))
      .sort(byName)
    return (
      <section data-testid="apps-results">
        {heading(<Search size={14} />, t('appsWindow.sections.results'), all.length)}
        {renderGrid(all)}
      </section>
    )
  }

  const lists = () => {
    // Recent fills ONE row; "View all" opens the rest (an identity-only shell has
    // nothing else to show, so it shows all of it).
    const expanded = recentOpen || historyOnly
    const recentShown = expanded ? recentViews : recentViews.slice(0, recentCols)
    const canExpand = !historyOnly && (recentViews.length > recentCols || moreHistory)
    // Clearing is offered once the whole list is on screen.
    const showClear = recentViews.length > 0 && (expanded || !canExpand)
    // Open on the tab the person last chose; the first time, on one that has apps.
    const current: CollectionId = collection ?? (mineViews.length === 0 && sharedViews.length > 0 ? 'shared' : 'mine')
    const tabs: Array<{ id: CollectionId; icon: React.ReactNode; label: string; count: number }> = [
      { id: 'mine', icon: <User size={13} />, label: t('appsWindow.sections.mine'), count: mineViews.length },
      { id: 'shared', icon: <UsersRound size={13} />, label: t('appsWindow.sections.shared'), count: sharedViews.length },
    ]
    return (
      <>
        {pinnedViews.length > 0 && (
          <section className="mb-7" data-testid="apps-section-pinned">
            {heading(<Star size={14} />, t('appsWindow.sections.pinned'), pinnedViews.length)}
            {renderGrid(pinnedViews)}
          </section>
        )}
        {(recentViews.length > 0 || moreHistory) && (
          <section className="mb-9" data-testid="apps-section-recent">
            {heading(<Clock size={14} />, t('appsWindow.sections.recent'), recentViews.length, (
              <div className="flex items-center gap-4">
                {showClear && (
                  <button onClick={() => void onClear()} className={linkBtn} data-testid="apps-clear-history">
                    {t('appsWindow.clear.button')}
                  </button>
                )}
                {canExpand && (
                  <button onClick={() => setRecentOpen(o => !o)} className={linkBtn} data-testid="apps-recent-toggle">
                    {recentOpen ? t('appsWindow.showLess') : t('driveHome.sections.viewAll')}
                    {!recentOpen && <ChevronRight size={13} />}
                  </button>
                )}
              </div>
            ))}
            {renderGrid(recentShown, recentGridRef)}
            {expanded && moreHistory && (
              <button onClick={() => void showMore()} disabled={loadingMore} className="mt-4 themed-btn-secondary rounded-lg px-3 py-1.5 text-xs disabled:opacity-60" data-testid="apps-show-more">
                {loadingMore ? t('appsWindow.loading') : t('appsWindow.showMore')}
              </button>
            )}
          </section>
        )}
        {!historyOnly && (mineViews.length > 0 || sharedViews.length > 0) && (
          <section>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div role="group" aria-label={t('appsWindow.title')} className="flex items-center gap-1.5 flex-wrap" data-testid="apps-tabs">
                {tabs.map(x => (
                  <button key={x.id} type="button" aria-pressed={current === x.id} onClick={() => setCollection(x.id)}
                    className="ws-filter-chip inline-flex items-center gap-1.5" data-testid={`apps-tab-${x.id}`}>
                    {x.icon}{x.label}<span className="opacity-60">{x.count}</span>
                  </button>
                ))}
              </div>
              {orgLabel && <span className="text-xs themed-text-muted">{t('appsWindow.inOrg', { org: orgLabel })}</span>}
            </div>
            {(() => {
              const views = current === 'mine' ? mineViews : sharedViews
              return (
                <div data-testid={current === 'mine' ? 'apps-group-mine' : 'apps-group-shared'}>
                  {views.length > 0 ? renderGrid(views) : <p className="text-sm themed-text-muted py-6">{t('appsWindow.tabEmpty')}</p>}
                  {current === 'shared' && (
                    <p className="mt-6 flex items-center gap-1.5 text-xs themed-text-muted" data-testid="apps-public-hint">
                      <Globe size={12} className="shrink-0" />
                      {t('appsWindow.publicLinksHint')}
                    </p>
                  )}
                </div>
              )
            })()}
          </section>
        )}
      </>
    )
  }

  const body = () => {
    if (loading) return <p className="text-sm themed-text-muted">{t('appsWindow.loading')}</p>
    if (history.isError && (historyOnly || projects.isError)) return <p className="text-sm themed-text-muted">{t('appsWindow.error')}</p>
    if (nothing && query.trim()) return <p className="text-sm themed-text-muted py-8 text-center">{t('appsWindow.noMatches', { query: query.trim() })}</p>
    if (nothing) {
      return (
        <div className="max-w-sm mx-auto py-10 text-center" data-testid="apps-empty">
          <PDAppIcon size={28} className="mx-auto mb-3 text-fuchsia-400" />
          <p className="text-sm themed-text-secondary">{t('appsWindow.empty.title')}</p>
          <p className="mt-1 text-sm themed-text-muted">{t('appsWindow.empty.hint')}</p>
        </div>
      )
    }
    return query.trim() ? results() : lists()
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto papan-thin-scroll" data-testid="apps-root">
      <div className="max-w-5xl mx-auto w-full px-4 py-6 sm:px-8 sm:py-8">
        <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
          <div className="flex items-center gap-2">
            <PDAppIcon size={18} className="text-fuchsia-400" />
            <h1 className="text-xl font-semibold themed-text">{t('appsWindow.title')}</h1>
          </div>
          {/* Everything this window leaves out (rename, move, icon, access…) lives there. */}
          {!historyOnly && (
            <button
              onClick={() => navigate(`${urlPrefix}/pdapps`)}
              className="themed-btn-secondary rounded-lg px-3 py-2 text-sm flex items-center gap-1.5"
              data-testid="apps-manage"
            >
              {t('appsWindow.manage')}
              <ArrowUpRight size={14} />
            </button>
          )}
        </div>
        <div className="relative max-w-sm mb-7">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 themed-text-muted pointer-events-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('appsWindow.search')}
            aria-label={t('appsWindow.search')}
            data-testid="apps-search"
            className="w-full themed-input ws-drive-pill rounded-lg pl-9 pr-3 py-2 text-sm"
          />
        </div>
        {body()}
      </div>

      {menu && (
        <FileContextMenu
          x={pos?.left ?? -9999}
          y={pos?.top ?? -9999}
          exiting={exiting}
          origin={origin}
          items={menuFor(menu.view.tile)}
          menuRef={menuRef}
          className={themeClass}
          testId="apps-menu"
        />
      )}
      {/* The same dialog a running app's Share button opens; it applies its own rules. */}
      {shareTarget && (
        <ShareLinksDialog
          fileId={shareTarget.id}
          fileName={shareTarget.name}
          filePath={shareTarget.path}
          isFolder={shareTarget.isFolder}
          // The drive card's rule for offering an App link; the dialog decides the rest.
          isAppEligible={isMiniappFolder(shareTarget.name)}
          onClose={() => setShareTarget(null)}
        />
      )}
    </div>
  )
}

/**
 * One app as a tile, as on the launcher: its icon (click runs it) and its name.
 * Everything else — Run, Share, Pin, Remove — is on the ⋯ menu (or right-click),
 * which shows on hover, and always on a touch screen.
 */
function AppTile({ view, menuOpen, onRun, onMenu, labels }: {
  view: TileView
  menuOpen: boolean
  onRun: () => void
  onMenu: (v: TileView, el: HTMLElement, at: { x: number; y: number } | null) => void
  labels: { more: string; pinned: string; viaLink: string; publicLink: string }
}) {
  const isLink = view.tile.type === 'entry' && view.tile.entry.kind === 'link'
  const BadgeIcon = view.badge === 'public' ? Globe : Link2
  const badgeLabel = view.badge === 'public' ? labels.publicLink : labels.viaLink
  return (
    <div
      className="group relative min-w-0"
      onContextMenu={(e) => { e.preventDefault(); onMenu(view, e.currentTarget, { x: e.clientX, y: e.clientY }) }}
      data-testid="apps-tile"
      data-kind={isLink ? 'link' : 'app'}
    >
      <button
        type="button"
        onClick={onRun}
        className="flex flex-col items-center gap-1.5 w-full min-w-0 p-2 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]"
        title={[view.name, view.hint].filter(Boolean).join(' — ')}
      >
        <span className="relative transition-transform duration-150 group-hover:-translate-y-px">
          <MiniappIcon
            spec={parseMiniappIcon(view.icon)}
            name={view.name}
            size={TILE_ICON_PX}
            refId={view.iconRef}
            rev={parseMiniappIconRev(view.icon)}
            className="chat-app-tile shrink-0"
          />
          {view.badge && (
            <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center themed-bg-secondary border themed-border" title={badgeLabel} data-testid="apps-tile-badge" data-badge={view.badge}>
              <BadgeIcon size={11} className="themed-text-muted" />
            </span>
          )}
        </span>
        <span className="w-full flex items-center justify-center gap-1 min-w-0 text-xs themed-text-secondary">
          {view.pinned && <Star size={10} className="shrink-0" style={{ color: '#f59e0b', fill: '#f59e0b' }} aria-label={labels.pinned} />}
          <span className="truncate">{view.name}</span>
        </span>
        {view.subtitle && <span className="w-full -mt-1 truncate text-center text-[10px] themed-text-muted">{view.subtitle}</span>}
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onMenu(view, e.currentTarget, null) }}
        aria-label={`${labels.more}: ${view.name}`}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        data-open={menuOpen}
        className="absolute top-0.5 left-[calc(50%+18px)] w-6 h-6 rounded-full flex items-center justify-center themed-bg-secondary border themed-border shadow-sm themed-text-muted hover:themed-text transition-opacity opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[open=true]:opacity-100 [@media(hover:none)]:opacity-100"
        data-testid="apps-tile-more"
      >
        <MoreHorizontal size={14} />
      </button>
    </div>
  )
}
